import { Code, ContentCopy, Delete, Edit, FileDownload } from "@mui/icons-material";
import { NavigateFunction, useNavigate } from "react-router";
import { AppFeatureLookup, defaultUnauthorizedMessage } from "../../../App/Constants/AbacConstants";
import {
  crmLiteUpgradeMessage,
  isActionAllowedByCampaignType,
} from "../../../App/Constants/CrmLiteConstants";
import { CampaignActionIds, CampaignQuickActions } from "../../../App/Enums/CampaignTypes";
import { useExportFormConfig } from "../../../App/Queries/CampaignQueries";
import { useFeaturePermission } from "../../../App/State/Hooks/abacHooks";
import { useIsSuperUser } from "../../../App/State/Hooks/useIsSuperUser";
import { useNotification } from "../../../App/State/Hooks/notificationHooks";
import { CampaignActionDictionary, ICampaign, ICampaignActionView } from "../../../App/Typings/Campaigns";

export const useCampaignActions = (campaign: ICampaign, actions: CampaignActionDictionary) => {
  const navigate = useNavigate();
  const { displayError } = useNotification();
  const accessEmbedScript = useFeaturePermission(AppFeatureLookup.CampaignsViewCampaignEmbedScript);
  const accessDuplicate = useFeaturePermission(AppFeatureLookup.CampaignsDuplicateCampaign);
  const accessEdit = useFeaturePermission(AppFeatureLookup.CampaignsEditCampaign);
  const accessDelete = useFeaturePermission(AppFeatureLookup.CampaignsDeleteCampaign);
  const isSuperUser = useIsSuperUser();
  const { mutate: exportFormConfig } = useExportFormConfig();
  const handleExportFormConfig = (request: { campaignId: string; formId: string; campaignName: string }) =>
    exportFormConfig(request, {
      onError: () => displayError("There was an error exporting the form configuration."),
    });

  let campaignActions: ICampaignActionView[] = [
    getEmbedAction(accessEmbedScript, campaign, actions),
    getDuplicateAction(accessDuplicate, campaign, navigate),
    getEditAction(accessEdit, campaign, navigate),
    getDeleteAction(accessDelete, campaign, actions),
  ];

  // Export is a super-user-only feature, and is hidden entirely (not just disabled) for everyone else
  if (isSuperUser) {
    campaignActions.push(getExportAction(campaign, handleExportFormConfig));
  }

  // Filter out Embed and Export actions if campaign has no forms (covers both an empty array and
  // forms being undefined/missing entirely, since the API doesn't always guarantee the former)
  if (!campaign.forms?.length) {
    campaignActions = campaignActions.filter(
      (action) => action.id !== CampaignActionIds.Embed && action.id !== CampaignActionIds.Export,
    );
  }

  return campaignActions;
};

const getEmbedAction = (
  hasAccess: boolean,
  campaign: ICampaign,
  actions: CampaignActionDictionary,
): ICampaignActionView => {
  const actionType = CampaignQuickActions.EmbedScript;
  const isActionAllowed = isActionAllowedByCampaignType(campaign.campaignType, actionType);
  const isEmbedDisabled = !hasAccess || !isActionAllowed;

  let tooltip = actionType.toString();
  if (!isActionAllowed) {
    tooltip = "Embed not available for this form type";
  } else if (!hasAccess) {
    tooltip = defaultUnauthorizedMessage;
  }

  return {
    id: CampaignActionIds.Embed,
    icon: <Code fontSize="small" />,
    tooltip,
    ariaLabel: "Get Embed Script",
    disabled: isEmbedDisabled,
    handler: () => !isEmbedDisabled && actions.embed(campaign),
  };
};

const getDuplicateAction = (
  hasAccess: boolean,
  campaign: ICampaign,
  navigate: NavigateFunction,
): ICampaignActionView => {
  const actionType = CampaignQuickActions.Duplicate;
  const isActionAllowed = isActionAllowedByCampaignType(campaign.campaignType, actionType);
  const isDuplicateDisabled = !hasAccess || !isActionAllowed;

  let tooltip = actionType.toString();
  if (!isActionAllowed) {
    tooltip = "Duplicate not available for this form type";
  } else if (!hasAccess) {
    tooltip = crmLiteUpgradeMessage;
  }

  return {
    id: CampaignActionIds.Duplicate,
    icon: <ContentCopy fontSize="small" />,
    tooltip,
    ariaLabel: "Delete Form",
    disabled: isDuplicateDisabled,
    handler: () =>
      !isDuplicateDisabled && navigate(`/leadcapture/forms/duplicate/${campaign.id}/${campaign.forms[0].id}`),
  };
};

const getEditAction = (
  hasAccess: boolean,
  campaign: ICampaign,
  navigate: NavigateFunction,
): ICampaignActionView => {
  const actionType = CampaignQuickActions.Edit;
  const isActionAllowed = isActionAllowedByCampaignType(campaign.campaignType, actionType);
  const isEditDisabled = !hasAccess || !isActionAllowed;

  let tooltip = actionType.toString();
  if (!isActionAllowed) {
    tooltip = "Edit not available for this form type";
  } else if (!hasAccess) {
    tooltip = defaultUnauthorizedMessage;
  }

  return {
    id: CampaignActionIds.Edit,
    icon: <Edit fontSize="small" />,
    tooltip,
    ariaLabel: "Edit Form",
    disabled: isEditDisabled,
    handler: () =>
      !isEditDisabled && navigate(`/leadcapture/forms/edit/${campaign.id}/${campaign.forms[0].id}`),
  };
};

const getDeleteAction = (
  hasAccess: boolean,
  campaign: ICampaign,
  actions: CampaignActionDictionary,
): ICampaignActionView => {
  const actionType = CampaignQuickActions.Delete;
  const isActionAllowed = isActionAllowedByCampaignType(campaign.campaignType, actionType);
  const isDeleteDisabled = !hasAccess || !isActionAllowed;

  let tooltip = actionType.toString();
  if (!isActionAllowed) {
    tooltip = "Delete not available for this form type";
  } else if (!hasAccess) {
    tooltip = crmLiteUpgradeMessage;
  }

  return {
    id: CampaignActionIds.Delete,
    icon: <Delete fontSize="small" />,
    tooltip,
    ariaLabel: "Delete Form",
    disabled: isDeleteDisabled,
    handler: () => !isDeleteDisabled && actions.delete(campaign),
  };
};

const getExportAction = (
  campaign: ICampaign,
  exportFormConfig: (request: { campaignId: string; formId: string; campaignName: string }) => void,
): ICampaignActionView => {
  const actionType = CampaignQuickActions.Export;
  const isActionAllowed = isActionAllowedByCampaignType(campaign.campaignType, actionType);
  const isExportDisabled = !isActionAllowed;

  const tooltip = isActionAllowed ? actionType.toString() : "Export not available for this form type";

  return {
    id: CampaignActionIds.Export,
    icon: <FileDownload fontSize="small" />,
    tooltip,
    ariaLabel: "Export Form Configuration",
    disabled: isExportDisabled,
    // Guards against `campaign.forms` being undefined rather than an empty array — the filter in
    // useCampaignActions only strips this action for an explicit empty array, so this stays the
    // last line of defense against `campaign.forms[0]` throwing
    handler: () => {
      const formId = campaign.forms?.[0]?.id;
      if (!isExportDisabled && formId) {
        exportFormConfig({ campaignId: campaign.id, formId, campaignName: campaign.name });
      }
    },
  };
};
